import { VOD_PERMISSIONS } from 'shared-permissions';
import { useFeatureContext } from 'contexts/FeatureContext.jsx';
import usePermissions from 'hooks/usePermissions.jsx';
import { useCalloutGroupDismissal } from 'hooks/calloutDismissals.js';
import useProductFeeds from 'pages/ProductFeed/hooks/useProductFeeds.js';
import useProductFeedsAccess from 'pages/ProductFeed/hooks/useProductFeedsAccess.js';
import { useOrgSettings } from 'components/OrgSelector/useOrgSettings.js';
import { INVENTORY } from 'constants/scopes.js';

export const PROMO_VARIANT = {
  CONNECT_FEED: 'connectFeed',
  FEED_PROCESSING: 'feedProcessing',
  TRY: 'try',
};

// One callout group per state: closing a state hides it on Home and in the
// Library, and the next state still shows once the org gets there.
export const PROMO_GROUPS = {
  [PROMO_VARIANT.CONNECT_FEED]: 'ai-product-tagging-connect-feed',
  [PROMO_VARIANT.FEED_PROCESSING]: 'ai-product-tagging-feed-processing',
  [PROMO_VARIANT.TRY]: 'ai-product-tagging-try',
};

// A feed that has finished a sync once has products to match, even while it
// syncs again.
const isFeedReady = (feed) => !!feed?.lastFetchedAt || feed?.status?.state === 'complete';

/**
 * Which AI product tagging promo to show, if any, until this user closes it
 * (per browser, with the other callouts):
 * - connectFeed: the org has no product feed, so tagging has nothing to match.
 * - feedProcessing: the org has feeds, but none has finished its first sync.
 * - try: a feed is ready, or the org lets tagging run with only manual products
 *   (`productSuggestionsAllowManualOnly`), so uploading a video is all it takes.
 * Only for an org with identity vectors and both AI product tagging flags.
 * Nothing shows while the feeds load, or when the user cannot act on the state.
 */
const useAiProductTaggingPromo = () => {
  const {
    features: { productIdentityVectorsEnabled, vodAiProductTaggingEnabled, vodAiProductTaggingUiEnabled },
  } = useFeatureContext();
  const { hasPermission } = usePermissions();
  const { productFeedsEnabled, productFeedsCreateEnabled } = useProductFeedsAccess();
  const { inventory, loading: settingsLoading } = useOrgSettings([INVENTORY]);

  const feedsEnabled = !!productIdentityVectorsEnabled && !!vodAiProductTaggingEnabled && !!vodAiProductTaggingUiEnabled
    && productFeedsEnabled;
  const { data: feeds, isSuccess: hasFeedsResult } = useProductFeeds({
    queryParams: { active: true },
    enabled: feedsEnabled,
  });
  const canTry = hasPermission(VOD_PERMISSIONS.VOD_MANAGE);

  let variant = null;
  if (feedsEnabled && hasFeedsResult && !settingsLoading) {
    const items = feeds?.items ?? [];
    if (!items.length && inventory?.productSuggestionsAllowManualOnly === true) variant = canTry ? PROMO_VARIANT.TRY : null;
    else if (!items.length) variant = productFeedsCreateEnabled ? PROMO_VARIANT.CONNECT_FEED : null;
    else if (!items.some(isFeedReady)) variant = PROMO_VARIANT.FEED_PROCESSING;
    else variant = canTry ? PROMO_VARIANT.TRY : null;
  }

  const { dismissed, dismiss } = useCalloutGroupDismissal(PROMO_GROUPS[variant]);

  return { variant: dismissed ? null : variant, dismiss };
};

export default useAiProductTaggingPromo;
